import React, { useState, useContext } from 'react';
import {
    Container, Grid, Paper, Typography, Box, Button, Chip,
    Divider, IconButton, LinearProgress, List, ListItem,
    ListItemIcon, ListItemText,
    Fade, CircularProgress, Alert, Tooltip
} from '@mui/material';
import {
    CloudUpload as CloudUploadIcon,
    CheckCircle as CheckCircleIcon,
    Agriculture as AgricultureIcon,
    Assignment as AssignmentIcon,
    Download as DownloadIcon,
    Image as ImageIcon,
    Delete as DeleteIcon,
    NavigateBefore as NavigateBeforeIcon,
    Science as ScienceIcon,
    Info as InfoIcon,
    TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { LanguageContext } from '../App';
import { getText } from '../utils/translations';
import API_BASE_URL from '../config/api';

const ImageOverlay = ({ src, hotspots, language }) => {
    return (
        <Box sx={{ position: 'relative', width: '100%', borderRadius: '12px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.1)' }}>
            <img src={src} alt="Analysis" style={{ width: '100%', display: 'block' }} />
            {hotspots && hotspots.map((spot, i) => (
                <Tooltip key={i} title={spot.type} arrow>
                    <Box
                        sx={{
                            position: 'absolute',
                            left: `${spot.x}%`,
                            top: `${spot.y}%`,
                            width: 24,
                            height: 24,
                            borderRadius: '50%',
                            border: '2px solid #f44336',
                            bgcolor: 'rgba(244, 67, 54, 0.3)',
                            transform: 'translate(-50%, -50%)',
                            animation: 'pulse 1.5s infinite',
                            cursor: 'help',
                            '@keyframes pulse': {
                                '0%': { transform: 'translate(-50%, -50%) scale(0.95)', boxShadow: '0 0 0 0 rgba(244, 67, 54, 0.7)' },
                                '70%': { transform: 'translate(-50%, -50%) scale(1.1)', boxShadow: '0 0 0 10px rgba(244, 67, 54, 0)' },
                                '100%': { transform: 'translate(-50%, -50%) scale(0.95)', boxShadow: '0 0 0 0 rgba(244, 67, 54, 0)' }
                            }
                        }}
                    />
                </Tooltip>
            ))}
        </Box>
    );
};

const CropQualityTest = () => {
    const { farmerId } = useParams();
    const navigate = useNavigate();
    const { language } = useContext(LanguageContext);

    const [selectedCrop, setSelectedCrop] = useState('Sugarcane');
    const [images, setImages] = useState([]);
    const [previews, setPreviews] = useState([]);
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [report, setReport] = useState(null);
    const [error, setError] = useState(null);
    const [uploadProgress, setUploadProgress] = useState(0);

    const crops = [
        { id: 'Sugarcane', name: 'Sugarcane (गन्ना)', icon: <AgricultureIcon />, disabled: false },
        { id: 'Wheat', name: 'Wheat (गेहूं) - Coming Soon', icon: <AgricultureIcon />, disabled: true },
        { id: 'Rice', name: 'Rice (चावल) - Coming Soon', icon: <AgricultureIcon />, disabled: true },
    ];

    // Compress and resize image for faster upload
    const compressImage = (file) => {
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                const img = new Image();
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    let width = img.width;
                    let height = img.height;

                    // Optimized for AI analysis - smaller size, faster upload
                    const MAX_WIDTH = 800;  // Reduced from 1200
                    const MAX_HEIGHT = 800;

                    if (width > height) {
                        if (width > MAX_WIDTH) {
                            height = (height * MAX_WIDTH) / width;
                            width = MAX_WIDTH;
                        }
                    } else {
                        if (height > MAX_HEIGHT) {
                            width = (width * MAX_HEIGHT) / height;
                            height = MAX_HEIGHT;
                        }
                    }

                    canvas.width = width;
                    canvas.height = height;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0, width, height);

                    // Aggressive compression for speed (75% quality)
                    canvas.toBlob((blob) => {
                        resolve(new File([blob], file.name, {
                            type: 'image/jpeg',
                            lastModified: Date.now(),
                        }));
                    }, 'image/jpeg', 0.75);  // Reduced from 0.85
                };
                img.src = e.target.result;
            };
            reader.readAsDataURL(file);
        });
    };

    const handleImageChange = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        setError(null);
        setIsAnalyzing(true); // Show immediate feedback

        // Check file sizes (warn if > 10MB before compression)
        const largeFiles = files.filter(f => f.size > 10 * 1024 * 1024);
        if (largeFiles.length > 0) {
            console.warn('Large files detected, compressing...');
        }

        // Compress images before adding
        const compressedFiles = await Promise.all(files.map(file => compressImage(file)));

        // Log compression ratio
        const originalSize = files.reduce((sum, f) => sum + f.size, 0);
        const compressedSize = compressedFiles.reduce((sum, f) => sum + f.size, 0);
        const ratio = ((1 - compressedSize / originalSize) * 100).toFixed(1);
        console.log(`Compressed ${ratio}% - ${(originalSize / 1024 / 1024).toFixed(2)}MB → ${(compressedSize / 1024 / 1024).toFixed(2)}MB`);

        setImages(prev => [...prev, ...compressedFiles]);

        // Generate previews
        compressedFiles.forEach(file => {
            const reader = new FileReader();
            reader.onloadend = () => {
                setPreviews(prev => [...prev, reader.result]);
            };
            reader.readAsDataURL(file);
        });

        setIsAnalyzing(false); // Reset after compression
    };

    const removeImage = (index) => {
        setImages(prev => prev.filter((_, i) => i !== index));
        setPreviews(prev => prev.filter((_, i) => i !== index));
    };

    const runAnalysis = async () => {
        if (images.length === 0) {
            setError('Please upload at least one image.');
            return;
        }

        setIsAnalyzing(true);
        setError(null);
        setUploadProgress(10);
        setReport(null);

        const formData = new FormData();
        images.forEach(image => {
            formData.append('files', image);
        });

        try {
            setUploadProgress(30);
            const response = await axios.post(`${API_BASE_URL}/api/sugarcane/quality-test/${farmerId}`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                onUploadProgress: (progressEvent) => {
                    const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    setUploadProgress(30 + (progress * 0.4));
                }
            });

            setUploadProgress(90);
            setTimeout(() => {
                setReport(response.data);
                setIsAnalyzing(false);
                setUploadProgress(0);
            }, 300);

        } catch (err) {
            console.error(err);
            setError(getText('failed_upload', language));
            setIsAnalyzing(false);
        }
    };

    const downloadPDF = () => {
        if (report && report.report_url) {
            window.open(`${API_BASE_URL}${report.report_url}`, '_blank');
        }
    };

    return (
        <Box sx={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)',
            pt: 4, pb: 8, px: 2
        }}>
            <Container maxWidth="xl">
                {/* Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, gap: 2 }}>
                    <IconButton onClick={() => navigate(-1)} sx={{ color: '#4CAF50', bgcolor: 'rgba(76, 175, 80, 0.1)' }}>
                        <NavigateBeforeIcon />
                    </IconButton>
                    <Typography variant="h4" sx={{ color: '#fff', fontWeight: 'bold' }}>
                        {getText('crop_quality_test', language)}
                    </Typography>
                    <Chip
                        label="AI PREVIEW"
                        size="small"
                        sx={{ bgcolor: '#4CAF50', color: '#fff', fontWeight: 'bold', height: 20, fontSize: '0.6rem' }}
                    />
                </Box>

                <Grid container spacing={3}>
                    {/* Left Panel: Crop Selection */}
                    <Grid item xs={12} md={3}>
                        <Paper className="premium-card" sx={{ p: 3, height: '100%' }}>
                            <Typography variant="h6" sx={{ color: '#4CAF50', mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <AssignmentIcon fontSize="small" /> {getText('select_crop', language)}
                            </Typography>
                            <List>
                                {crops.map((crop) => (
                                    <ListItem
                                        key={crop.id}
                                        button
                                        disabled={crop.disabled}
                                        onClick={() => setSelectedCrop(crop.id)}
                                        sx={{
                                            borderRadius: '12px',
                                            mb: 1,
                                            bgcolor: selectedCrop === crop.id ? 'rgba(76, 175, 80, 0.15)' : 'transparent',
                                            border: selectedCrop === crop.id ? '1px solid #4CAF50' : '1px solid transparent',
                                            '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' }
                                        }}
                                    >
                                        <ListItemIcon sx={{ color: selectedCrop === crop.id ? '#4CAF50' : '#888' }}>
                                            {crop.icon}
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={crop.name}
                                            sx={{ color: selectedCrop === crop.id ? '#fff' : '#888' }}
                                        />
                                        {selectedCrop === crop.id && <CheckCircleIcon sx={{ color: '#4CAF50', fontSize: 18 }} />}
                                    </ListItem>
                                ))}
                            </List>
                        </Paper>
                    </Grid>

                    {/* Middle Panel: Upload & Action */}
                    <Grid item xs={12} md={report ? 4 : 9}>
                        <Paper className="premium-card" sx={{ p: 3, height: '100%', minHeight: 400, display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="h6" sx={{ color: '#4CAF50', mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CloudUploadIcon fontSize="small" /> {getText('upload_harvest_images', language)}
                            </Typography>

                            <Box
                                sx={{
                                    flexGrow: 1,
                                    border: '2px dashed rgba(76, 175, 80, 0.3)',
                                    borderRadius: '16px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    p: 4,
                                    mb: 3,
                                    bgcolor: 'rgba(0,0,0,0.2)',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    '&:hover': { borderColor: '#4CAF50', bgcolor: 'rgba(76, 175, 80, 0.05)' }
                                }}
                                onClick={() => document.getElementById('image-upload').click()}
                            >
                                <input
                                    type="file"
                                    id="image-upload"
                                    multiple
                                    hidden
                                    onChange={handleImageChange}
                                    accept="image/*"
                                />
                                <CloudUploadIcon sx={{ fontSize: 48, color: '#4CAF50', mb: 2, opacity: 0.7 }} />
                                <Typography sx={{ color: '#888', textAlign: 'center' }}>
                                    {getText('drag_drop', language)}
                                </Typography>
                            </Box>

                            {/* Previews */}
                            {previews.length > 0 && (
                                <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 2, mb: 3 }}>
                                    {previews.map((src, idx) => (
                                        <Box key={idx} sx={{ position: 'relative', minWidth: 100, height: 100 }}>
                                            <img src={src} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px' }} />
                                            <IconButton
                                                size="small"
                                                onClick={(e) => { e.stopPropagation(); removeImage(idx); }}
                                                sx={{ position: 'absolute', top: -10, right: -10, bgcolor: '#f44336', color: '#fff', '&:hover': { bgcolor: '#d32f2f' } }}
                                            >
                                                <DeleteIcon fontSize="inherit" />
                                            </IconButton>
                                        </Box>
                                    ))}
                                </Box>
                            )}

                            {error && <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }}>{error}</Alert>}

                            {isAnalyzing && (
                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="caption" sx={{ color: '#4CAF50', mb: 1, display: 'block' }}>
                                        {getText('uploading', language)}... {Math.round(uploadProgress)}%
                                    </Typography>
                                    <LinearProgress variant="determinate" value={uploadProgress} sx={{ height: 6, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.05)', '& .MuiLinearProgress-bar': { bgcolor: '#4CAF50' } }} />
                                </Box>
                            )}

                            <Button
                                variant="contained"
                                fullWidth
                                size="large"
                                disabled={images.length === 0 || isAnalyzing}
                                onClick={runAnalysis}
                                startIcon={isAnalyzing ? <CircularProgress size={20} color="inherit" /> : <ScienceIcon />}
                                sx={{
                                    py: 2,
                                    borderRadius: '12px',
                                    bgcolor: '#4CAF50',
                                    fontWeight: 'bold',
                                    '&:hover': { bgcolor: '#45a049' }
                                }}
                            >
                                {isAnalyzing ? 'Analyzing Quality...' : getText('analyze_quality', language)}
                            </Button>
                        </Paper>
                    </Grid>

                    {/* Right Panel: Analysis Results */}
                    {report && (
                        <Grid item xs={12} md={5}>
                            <Fade in={!!report}>
                                <Box>
                                    <Paper className="premium-card" sx={{ p: 4, mb: 3, border: '1px solid rgba(76, 175, 80, 0.4)' }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                                            <Box>
                                                <Typography variant="h5" sx={{ color: '#fff', fontWeight: 'bold' }}>
                                                    {getText('quality_assessment', language)}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: '#888' }}>
                                                    Ref ID: HAR-{report.timestamp.replace(/[:-\s]/g, '').slice(-8)}
                                                </Typography>
                                            </Box>
                                            {report.analysis_results[0].is_valid !== false && (
                                                <Chip
                                                    label={report.report.overall_grade}
                                                    color={report.report.aggregate_score > 75 ? "success" : "warning"}
                                                    sx={{ fontWeight: 'bold' }}
                                                />
                                            )}
                                        </Box>

                                        {report.is_partial && (
                                            <Alert
                                                severity="info"
                                                sx={{ mb: 3, bgcolor: 'rgba(33, 150, 243, 0.1)', color: '#fff', border: '1px solid rgba(33, 150, 243, 0.2)', borderRadius: '12px' }}
                                            >
                                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                    {getText('partial_features_detected', language) || "Partial Sugarcane Features Detected"}
                                                </Typography>
                                                <Typography variant="caption">
                                                    {getText('reduced_confidence_msg', language) || "Analysis performed with reduced confidence due to partial visual traits."}
                                                </Typography>
                                            </Alert>
                                        )}

                                        {report.report.is_valid_report === false && (
                                            <Alert
                                                severity="error"
                                                sx={{ mb: 3, bgcolor: 'rgba(244, 67, 54, 0.1)', color: '#fff', border: '1px solid rgba(244, 67, 54, 0.2)', borderRadius: '12px' }}
                                            >
                                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                    {getText('invalid_crop_image', language) || "Real Crop Image Required"}
                                                </Typography>
                                                <Typography variant="caption">
                                                    {report.report.insights[0]}
                                                </Typography>
                                            </Alert>
                                        )}

                                        {report.report.aggregate_score > 85 && report.report.is_valid_report !== false && !report.is_partial && (
                                            <Alert
                                                icon={<CheckCircleIcon sx={{ color: '#4CAF50' }} />}
                                                sx={{ mb: 3, bgcolor: 'rgba(76, 175, 80, 0.1)', color: '#fff', border: '1px solid rgba(76, 175, 80, 0.2)', borderRadius: '12px' }}
                                            >
                                                {getText('high_quality_encourage', language)}
                                            </Alert>
                                        )}

                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 4 }}>
                                            <Typography variant="caption" sx={{ color: '#888' }}>
                                                AI Confidence:
                                            </Typography>
                                            <Chip
                                                label={`${report.report?.avg_confidence || report.avg_confidence || (report.analysis_results && report.analysis_results[0]?.confidence) || 0}%`}
                                                size="small"
                                                variant="outlined"
                                                sx={{ color: '#4CAF50', borderColor: 'rgba(76, 175, 80, 0.3)', fontWeight: 'bold' }}
                                            />
                                        </Box>

                                        <Divider sx={{ mb: 4, opacity: 0.1 }} />

                                        {/* Image Insights with Overlay */}
                                        <Typography variant="h6" sx={{ color: '#4CAF50', mb: 2, fontSize: '1rem', display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <ImageIcon fontSize="small" /> {getText('visual_stress_highlights', language)}
                                        </Typography>
                                        <Box sx={{ mb: 4 }}>
                                            {report.analysis_results[0].is_valid !== false ? (
                                                <>
                                                    <ImageOverlay
                                                        src={previews[0]}
                                                        hotspots={report.analysis_results[0].hotspots}
                                                        language={language}
                                                    />
                                                    {report.analysis_results[0].hotspots?.length > 0 && (
                                                        <Typography variant="caption" sx={{ color: '#888', mt: 1, display: 'block', fontStyle: 'italic', textAlign: 'center' }}>
                                                            {report.analysis_results[0].visual_description}
                                                        </Typography>
                                                    )}
                                                </>
                                            ) : (
                                                <Box sx={{
                                                    height: 250,
                                                    bgcolor: 'rgba(255,255,255,0.02)',
                                                    borderRadius: '12px',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    border: '1px dashed rgba(255,255,255,0.1)'
                                                }}>
                                                    <ScienceIcon sx={{ fontSize: 48, color: 'rgba(255,255,255,0.1)', mb: 2 }} />
                                                    <Typography variant="caption" sx={{ color: '#666' }}>
                                                        Awaiting Valid Sugarcane Photo
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>

                                        {report.analysis_results[0].is_valid !== false && (
                                            <>
                                                <Grid container spacing={4} sx={{ mb: 4 }}>
                                                    <Grid item xs={6}>
                                                        <Box sx={{ p: 2, bgcolor: 'rgba(255,255,255,0.03)', borderRadius: '12px' }}>
                                                            <Typography variant="caption" sx={{ color: '#888', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                <TrendingUpIcon fontSize="inherit" /> {getText('sugar_potential', language)}
                                                            </Typography>
                                                            <Typography variant="h6" sx={{ color: '#4CAF50', mt: 1 }}>
                                                                {report.report.sugar_potential}
                                                            </Typography>
                                                        </Box>
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        <Box sx={{ p: 2, bgcolor: 'rgba(255,255,255,0.03)', borderRadius: '12px' }}>
                                                            <Typography variant="caption" sx={{ color: '#888', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                <AssignmentIcon fontSize="inherit" /> {getText('maturity_assessment', language)}
                                                            </Typography>
                                                            <Typography variant="h6" sx={{ color: '#fff', mt: 1 }}>
                                                                {report.report.maturity_assessment}
                                                            </Typography>
                                                        </Box>
                                                    </Grid>
                                                </Grid>

                                                <Typography variant="h6" sx={{ color: '#4CAF50', mb: 2, fontSize: '1rem', display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <InfoIcon fontSize="small" /> {getText('ai_observations', language)}
                                                </Typography>
                                                <Box sx={{ mb: 4 }}>
                                                    {report.report.insights && report.report.insights.length > 0 ? (
                                                        report.report.insights.map((insight, i) => (
                                                            <Typography key={i} variant="body2" sx={{ color: '#e0e0e0', mb: 1, pl: 2, borderLeft: '2px solid #4CAF50' }}>
                                                                {insight}
                                                            </Typography>
                                                        ))
                                                    ) : (
                                                        <Typography variant="body2" sx={{ color: '#e0e0e0' }}>
                                                            {getText('standard_growth_traits', language) || 'Consistent quality traits detected for this variety.'}
                                                        </Typography>
                                                    )}
                                                </Box>

                                                {/* Actionable Impact Section */}
                                                <Paper sx={{ p: 2, bgcolor: 'rgba(76, 175, 80, 0.05)', borderRadius: '12px', border: '1px solid rgba(76, 175, 80, 0.2)', mb: 4 }}>
                                                    <Typography variant="h6" sx={{ color: '#4CAF50', mb: 2, fontSize: '0.9rem', fontWeight: 'bold' }}>
                                                        🔍 {getText('what_this_means', language)}
                                                    </Typography>
                                                    <List size="small" disablePadding>
                                                        <ListItem sx={{ px: 0, py: 0.5 }}>
                                                            <ListItemText
                                                                primary={<Typography variant="caption" sx={{ color: '#888' }}>{getText('sugar_recovery_impact', language)}</Typography>}
                                                                secondary={<Typography variant="body2" sx={{ color: '#fff' }}>{report.analysis_results[0].impact?.sugar || 'Standard recovery expected'}</Typography>}
                                                            />
                                                        </ListItem>
                                                        <ListItem sx={{ px: 0, py: 0.5 }}>
                                                            <ListItemText
                                                                primary={<Typography variant="caption" sx={{ color: '#888' }}>{getText('price_deduction_risk', language)}</Typography>}
                                                                secondary={<Typography variant="body2" sx={{ color: '#fff' }}>{report.analysis_results[0].impact?.price || 'No immediate price risk'}</Typography>}
                                                            />
                                                        </ListItem>
                                                        <ListItem sx={{ px: 0, py: 0.5 }}>
                                                            <ListItemText
                                                                primary={<Typography variant="caption" sx={{ color: '#888' }}>{getText('next_steps_advice', language)}</Typography>}
                                                                secondary={<Typography variant="body2" sx={{ color: '#4CAF50', fontWeight: 'bold' }}>{report.analysis_results[0].impact?.next || 'Follow standard harvest practices'}</Typography>}
                                                            />
                                                        </ListItem>
                                                    </List>
                                                </Paper>

                                                <Button
                                                    variant="outlined"
                                                    fullWidth
                                                    onClick={downloadPDF}
                                                    startIcon={<DownloadIcon />}
                                                    sx={{
                                                        py: 1.5,
                                                        borderRadius: '12px',
                                                        borderColor: '#4CAF50',
                                                        color: '#4CAF50',
                                                        '&:hover': { bgcolor: 'rgba(76, 175, 80, 0.1)', borderColor: '#4CAF50' }
                                                    }}
                                                >
                                                    {getText('download_report', language)}
                                                </Button>
                                            </>
                                        )}

                                        <Typography variant="caption" sx={{ color: '#555', mt: 3, display: 'block', textAlign: 'center', fontStyle: 'italic' }}>
                                            {report.report.disclaimer}
                                        </Typography>
                                    </Paper>
                                </Box>
                            </Fade>
                        </Grid>
                    )}
                </Grid>
            </Container>
        </Box>
    );
};

export default CropQualityTest;
